import { useMDSContext } from '@metservice/use-mds';
import { getDatabilityIndex } from '@metservicedev/platforms-services-interface';
import { trimNcoJson } from '@mono/ncojson-util';
import type { basicDataset } from '@mono/types/src/mds';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import type { DateTimeUnit } from 'luxon';
import { type Dispatch, useEffect, useMemo, useReducer, useRef } from 'react';

import type { GetCfjsonResponse } from '@metservicedev/platforms-services-interface/src/services/movIntegrationMDS/MovIntegrationMDSModel';
import { toHash } from '@mono/to-hash';
import { transformTimeRangeToDurations } from 'utils/timeRangeMagic';
import type IWidgetBlock from '../WidgetBlocks/types/IWidgetBlock';
import type { ConfigBlockState, IWidgetState, WidgetState } from '../WidgetBlocks/types/IWidgetBlock';
import * as reduxActions from './widgetblock/actions';
import type { WidgetBlockAction } from './widgetblock/actions/types';

interface IUseWidgetBlockReducerProps<BlockState extends ConfigBlockState, ReduxState extends WidgetState<IWidgetState<Record<string, any>>>> {
    widgetBlock: IWidgetBlock<BlockState, ReduxState>;
    viewblockState: BlockState;
    defaultState?: ReduxState; // default undefined
    updateInterval?: number;
    timezone?: string;
    date?: Date; // default now
    widgetDefinition?: BlockState; // @deprecated use viewblockState instead, may be removed in next major release
    datasets?: basicDataset[];
    cfjsons?: GetCfjsonResponse[];
}

const UPDATE_INTERVAL = 60 * 60 * 3 * 1000; // 3 hours by default
const DEFAULT_DATE = new Date();
const DEFAULT_TIMEZONE = DateTime.local().zone.name;

const UNTRIMMED_WIDGETBLOCKS = ['TextTimeseriesPoint2dTable', 'spectraTimeSeries', 'lineGraph']; //Widgetblocks we don't want to trim here, either they do it themselves or they don't need to

const useWidgetBlockReducer = <BlockState extends ConfigBlockState, ReduxState extends WidgetState>({
    widgetBlock,
    viewblockState: viewblockStateProp,
    defaultState,
    updateInterval = UPDATE_INTERVAL,
    timezone = DEFAULT_TIMEZONE,
    date = DEFAULT_DATE,
    widgetDefinition,
    datasets,
    cfjsons,
}: IUseWidgetBlockReducerProps<BlockState, ReduxState>): [ReduxState | undefined, Dispatch<WidgetBlockAction>] => {
    const { capabilities } = useMDSContext();
    const [state, dispatch] = useReducer(widgetBlock.reducer, defaultState!);
    const viewblockState = viewblockStateProp || widgetDefinition;
    const databilities = viewblockState.databilities;
    const mounted = useRef(false);

    const updateWidgetTemplateDebounced = useMemo(
        () =>
            debounce(
                (dispatchDebounced: typeof dispatch, viewblockState: typeof viewblockStateProp) =>
                    dispatchDebounced(reduxActions.widgetTemplateLoaded(null, null, viewblockState)),
                500
            ),
        []
    );

    const fetchData = () => {
        if (capabilities.length === 0) return;
        if (!datasets) return;
        if (!cfjsons) return;

        const fetchDataResponse = databilities.map((datability) => {
            const dataset = datasets.find((dataset) => dataset.id === datability.datasetId);
            const databilityIndex = getDatabilityIndex(datability);
            const cfjson = cfjsons.find((cfjson) => cfjson.databilityIndex === databilityIndex)?.cfjson;
            const capability = capabilities.find((capability) => capability.id === datability.capabilityId);
            if (!cfjson || !datability || !datability.timeRange) {
                return {
                    databilityId: datability.id,
                    databilityIndex,
                    cfjson: null,
                    dataset,
                    datasetId: dataset?.id,
                    capability,
                    capabilityId: capability?.id,
                    error: 'unable to get cfjson',
                };
            }

            const { start: startValue, end: endValue } = transformTimeRangeToDurations(datability.timeRange);

            const startUnit: DateTimeUnit = (Object.keys(startValue.toObject())[0].slice(0, -1) as DateTimeUnit) || 'day';
            const endUnit: DateTimeUnit = (Object.keys(endValue.toObject())[0].slice(0, -1) as DateTimeUnit) || 'day';
            const start = DateTime.utc()
                .setZone(timezone ?? 'utc')
                .startOf(startUnit)
                .plus(startValue)
                .toISO();
            const end = DateTime.utc()
                .setZone(timezone ?? 'utc')
                .startOf(endUnit)
                .plus(endValue)
                .toISO();

            const result = {
                databilityId: datability.id,
                databilityIndex,
                cfjson: UNTRIMMED_WIDGETBLOCKS.find((type) => type === widgetBlock.type) ? cfjson : trimNcoJson(cfjson, { start, end }),
                dataset,
                datasetId: dataset?.id,
                capability,
                capabilityId: capability?.id,
            };
            return result;
        });
        dispatch(reduxActions.widgetDataLoaded(null, null, fetchDataResponse));
    };

    useEffect(() => {
        mounted.current = true;
        if (capabilities.length === 0) return;

        dispatch(reduxActions.setTime(null, null, date.toISOString(), timezone));
        dispatch(reduxActions.setCapabilitiesLoaded(null, null, capabilities));

        return () => {
            mounted.current = false;
        };
    }, [capabilities.length]);

    useEffect(() => {
        dispatch(reduxActions.setTime(null, null, date.toISOString(), timezone));
    }, [timezone, date]);

    useEffect(() => fetchData(), [toHash([databilities, capabilities.length, datasets, cfjsons])]);

    useEffect(() => {
        const interval = setInterval(() => fetchData(), updateInterval);

        return () => {
            clearInterval(interval);
        };
    }, [updateInterval, capabilities.length]);

    useEffect(() => {
        const migrated = widgetBlock.migrateState?.(viewblockState) || viewblockState;

        updateWidgetTemplateDebounced(dispatch, migrated);
    }, [viewblockState]);

    return [state, dispatch];
};

export { type IUseWidgetBlockReducerProps, useWidgetBlockReducer };
